import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ExternalLink, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

/* ---------------- Region / Language ---------------- */

type LangCode = "en" | "es" | "it";

function RegionLanguageMenu({
    value,
    onChange,
}: {
    value: LangCode;
    onChange: (lang: LangCode) => void;
}) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    const options: Array<{ country: string; label: string; code: LangCode }> = [
        { country: "United States", label: "English", code: "en" },
        { country: "United States", label: "Spanish", code: "es" },
        { country: "United States", label: "Italian", code: "it" },
    ];

    const current =
        options.find((o) => o.code === value) ?? {
            country: "United States",
            label: "English",
            code: "en",
        };

    // Close on outside click / ESC
    useEffect(() => {
        const onDocDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDocDown);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDocDown);
            document.removeEventListener("keydown", onEsc);
        };
    }, []);

    return (
        <div className="relative">
            <button
                ref={btnRef}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {current.label}
                <ChevronDown
                    size={14}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <div
                    ref={panelRef}
                    className="absolute right-0 mt-2 w-56 rounded-md border bg-white shadow-xl ring-1 ring-black/5 z-[10000]"
                >
                    {options.map((o) => (
                        <button
                            key={o.code}
                            onClick={() => {
                                onChange(o.code);
                                setOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${value === o.code ? "bg-slate-100 font-medium" : ""
                                }`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------------- Menu Model ---------------- */

const MENU = [
    {
        label: "Services",
        children: [
            { label: "Application Services", to: "/services/application" },
            { label: "Data Maintenance", to: "/services/data" },
        ],
    },
    {
        label: "Products",
        children: [
            { label: "ExamsPro", to: "/solutions/examspro" },
            { label: "DactylScan", to: "/solutions/dactylscan" },
        ],
    },
    {
        label: "Consulting",
        children: [{ label: "Avaya OD", to: "/solutions/avaya" }],
    },
    {
        label: "Industries",
        children: [
            { label: "Human Resources", to: "/industries/hr" },
            { label: "Government & Agencies", to: "/industries/government" },
            { label: "Education", to: "/industries/education" },
            { label: "Health Care", to: "/industries/health" },
        ],
    },
    {
        label: "Insights",
        children: [{ label: "Creativity Lab", to: "/insights/creativity-lab" }],
    },
    { label: "About Us", to: "/about" },
];

/* ---------------- Navbar ---------------- */

export default function Navbar() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const { i18n } = useTranslation();
    const headerRef = useRef<HTMLElement | null>(null);
    const location = useLocation();
    const navigate = useNavigate(); // ✅ navigation enabled

    // Close dropdown on outside click
    useEffect(() => {
        const onDocDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (headerRef.current?.contains(t)) return;
            setOpenMenu(null);
        };
        const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpenMenu(null);
        document.addEventListener("mousedown", onDocDown);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDocDown);
            document.removeEventListener("keydown", onEsc);
        };
    }, []);

    // Lock background scroll when mobile menu open
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOpen]);

    // Close mobile menu on navigation
    useEffect(() => {
        setMobileOpen(false);
        document.body.style.overflow = "";
    }, [location.pathname]);

    return (
        <header
            ref={headerRef}
            className="fixed top-0 left-0 w-full z-[9999] bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b shadow-sm"
        >
            <nav className="mx-auto max-w-7xl px-4 lg:px-6">
                <div className="flex h-14 items-center justify-between gap-3">
                    {/* Brand */}
                    <div className="min-w-0 flex items-center gap-3">
                        <Link to="/" className="flex items-center gap-2 min-w-0">
                            <img src="/logo-navbar.png?v=3" alt="SecureID" className="h-7 w-auto" />
                            <span className="text-slate-900 font-semibold tracking-tight">
                                SecureID
                            </span>
                        </Link>
                    </div>

                    {/* Desktop Nav */}
                    <ul className="hidden md:flex items-center gap-6 text-sm text-slate-700">
                        {MENU.map((item) => {
                            const key = item.label;
                            return (
                                <li key={key} className="relative">
                                    {"children" in item ? (
                                        <>
                                            <button
                                                onClick={() =>
                                                    setOpenMenu(openMenu === key ? null : key)
                                                }
                                                className="inline-flex items-center gap-1.5 py-2 hover:text-slate-900"
                                            >
                                                {item.label}
                                                <ChevronDown
                                                    size={14}
                                                    className={`transition-transform ${openMenu === key ? "rotate-180" : ""
                                                        }`}
                                                />
                                            </button>
                                            {openMenu === key && item.children && (
                                                <div className="absolute left-0 mt-2 bg-white border border-gray-100 rounded-lg shadow-lg w-56 z-[10000]">
                                                    <ul className="py-2 text-sm text-gray-700">
                                                        {item.children.map((child) => (
                                                            <li key={child.label}>
                                                                <NavLink
                                                                    to={child.to}
                                                                    onClick={() => setOpenMenu(null)}
                                                                    className="block px-4 py-2 hover:bg-gray-50"
                                                                >
                                                                    {child.label}
                                                                </NavLink>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <NavLink
                                            to={item.to!}
                                            className={({ isActive }) =>
                                                [
                                                    "inline-flex items-center gap-1.5 py-2 transition-colors",
                                                    isActive
                                                        ? "text-slate-900 font-medium"
                                                        : "hover:text-slate-900",
                                                ].join(" ")
                                            }
                                        >
                                            {item.label}
                                        </NavLink>
                                    )}
                                </li>
                            );
                        })}

                        {/* 🔤 Language Selector */}
                        <li>
                            <RegionLanguageMenu
                                value={(i18n.language as LangCode) || "en"}
                                onChange={(lng) => i18n.changeLanguage(lng)}
                            />
                        </li>

                        {/* 🌐 Investors */}
                        <li>
                            <Link
                                to="/investors"
                                className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
                            >
                                Investors <ExternalLink size={14} className="opacity-70" />
                            </Link>
                        </li>

                        {/* 🔍 Search */}
                        <li>
                            <button
                                className="p-1.5 rounded hover:bg-indigo-50 hover:text-indigo-700 transition"
                                aria-label="Search"
                                onClick={() => navigate("/search")}
                            >
                                <Search size={20} className="text-slate-700" />
                            </button>
                        </li>
                    </ul>

                    {/* Mobile burger toggle */}
                    <button
                        className="md:hidden p-2 rounded hover:bg-slate-100"
                        onClick={() => setMobileOpen((prev) => !prev)}
                        aria-label={mobileOpen ? "Close menu" : "Open menu"}
                    >
                        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </nav>

            {/* ✅ Mobile Drawer */}
            {mobileOpen && (
                <div className="md:hidden bg-white border-t border-gray-200 shadow-inner max-h-[85vh] overflow-y-auto px-6 py-4 space-y-4">
                    {MENU.map((item) => (
                        <div key={item.label}>
                            {"children" in item && item.children ? (
                                <>
                                    <div className="text-[15px] font-semibold text-gray-900 mb-2">
                                        {item.label}
                                    </div>
                                    <ul className="pl-3 mt-2 space-y-1 bg-indigo-50 rounded-md border border-indigo-100">
                                        {item.children.map((child) => (
                                            <li key={child.label}>
                                                <NavLink
                                                    to={child.to}
                                                    onClick={() => setMobileOpen(false)}
                                                    className={({ isActive }) =>
                                                        [
                                                            "block w-full rounded-md px-3 py-2 text-[15px] transition font-medium !text-indigo-900",
                                                            isActive
                                                                ? "!bg-indigo-100 !text-indigo-800 font-semibold"
                                                                : "hover:!bg-indigo-100 hover:!text-indigo-700",
                                                        ].join(" ")
                                                    }
                                                >
                                                    {child.label}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <NavLink
                                    to={item.to}
                                    onClick={() => setMobileOpen(false)}
                                    className={({ isActive }) =>
                                        [
                                            "block w-full rounded-md px-3 py-2 text-[15px] transition font-medium",
                                            isActive
                                                ? "bg-indigo-50 text-indigo-700 font-semibold"
                                                : "text-gray-800 hover:bg-indigo-50 hover:text-indigo-700",
                                        ].join(" ")
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            )}
                        </div>
                    ))}

                    {/* 🔍 Search (mobile) */}
                    <div className="pt-4 border-t border-gray-200">
                        <button
                            onClick={() => {
                                setMobileOpen(false);
                                navigate("/search");
                            }}
                            className="w-full text-left px-3 py-2 text-indigo-700 font-medium hover:bg-indigo-50 rounded-md flex items-center gap-2"
                        >
                            <Search size={18} />
                            Search
                        </button>
                    </div>

                    {/* 🌐 Investors (mobile) */}
                    <div>
                        <Link
                            to="/investors"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-2 text-indigo-700 font-medium px-3 py-2 hover:bg-indigo-50 rounded-md"
                        >
                            <ExternalLink size={16} />
                            Investors
                        </Link>
                    </div>

                    {/* 🌎 Language Selector */}
                    <div className="pt-3 border-t mt-2 space-y-3 !text-indigo-900">
                        <div className="px-2">
                            <RegionLanguageMenu
                                value={(i18n.language as LangCode) || "en"}
                                onChange={(lng) => {
                                    i18n.changeLanguage(lng);
                                    setMobileOpen(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
